import DocumentCreationButton from 'Components/DocumentCreationButton';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import { Item } from 'Components/Grid';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryRowListing from 'Components/HistoryRowListing';
import HistorySection from 'Components/HistorySection';
import PlatformConditional from 'Components/PlatformConditional';
import FormEditingHandler from 'Containers/FormEditingHandler';
import { sortPartialDate, findLatest, isPartialDate, parsePatientDemographics } from 'neuro-utils';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { useAppSelector } from 'Store/index';
import { formatPartialDate } from 'Utility/date';
import { filterByDocumentType, filterDocumentsByType } from 'Utility/documentHandling';
import { platforms } from '../../../config/platformSettings';
import { isEnded } from '../../../utility/isEnded';
import { getSectionTitle } from '../../../utility/randomUtil';
import { getMedicationNames, stateToHistoryProps, getDiagnosisCodes } from '../utils';
import Form from './Form';
import HistoryRowDataComorbidity from './HistoryRowData/Comorbidity';
import HistoryRowDataContraception from './HistoryRowData/Contraception';
import HistoryRowDataHospitalization from './HistoryRowData/Hospitalization';
import MgravisHospitalizationHistory from './HistoryRowData/MgravisHospitalization';
import HistoryRowDataPregnancy from './HistoryRowData/Pregnancy';
import HistoryRowDataSickLeave from './HistoryRowData/SickLeave';

import type { TComorbidityDocumentType, TRelevantDocumentType } from '../utils';

interface ComorbidityHistoryProps extends Pick<IJWTBody, 'visitreason' | 'patientssn'> {
  documents: IDocumentWithTypeV2<TComorbidityDocumentType>[];
  startEdit: StartEdit;
  diagnosis: string[];
}

const _History = ({
  documents,
  startEdit,
  visitreason,
  patientssn,
  diagnosis,
}: ComorbidityHistoryProps): React.JSX.Element => {
  const comorbidities = filterByDocumentType(documents, 'comorbidity');
  const hospitalizations = filterByDocumentType(documents, 'hospitalization');
  const mgravisHospitalizations = hospitalizations.filter(
    h => h.hospitalizationType === 'acute' && h.classification?.icd10code === 'G70',
  );
  const pregnancies = filterByDocumentType(documents, 'pregnancy');
  const contraceptions = filterByDocumentType(documents, 'contraception');
  const sickLeaves = filterByDocumentType(documents, 'sickLeave');

  const platform = useSelector((s: { session: ISessionStore }) => s.session.platforms?.selected);

  const { ssn, ssnLocalization } = useAppSelector(s => s.patient.data) ?? {};
  const demographics = ssn && ssnLocalization ? parsePatientDemographics(ssnLocalization, { ssn }) : undefined;

  // conditions for restricted operations
  const isRetrospective = visitreason === 'retrospectiveDataTransfer';
  const isFemale = demographics?.gender === 'female';
  const ongoingPregnancy = findLatest<IDocumentWithTypeV2<'pregnancy'>>(
    pregnancies,
    (pregnancy: IDocumentWithTypeV2<'pregnancy'>) => !isPartialDate(pregnancy.endDate),
    { singleton: true }, // most recent record disregards any old records
  );
  const ongoingContraception = findLatest<IDocumentWithTypeV2<'contraception'>>(
    contraceptions,
    (c: IDocumentWithTypeV2<'contraception'>) => !isPartialDate(c.endDate),
    { singleton: false }, // older contraception may be ongoing even when more recent has ended
  );

  /** Extra sections for epilepsy. */
  const extraEpSections = !isFemale
    ? // none specified for males
      []
    : // sections for females
      ([
        { documents: pregnancies, type: 'pregnancy', headerText: 'pregnancies', buttonText: 'newPregnancy' },
        {
          documents: contraceptions,
          type: 'contraception',
          headerText: 'usesOfContraceptives',
          buttonText: 'newUseOfContraceptive',
        },
      ] as const);

  const disableAddNewOf = (section: 'pregnancy' | 'contraception'): boolean => {
    if (isRetrospective) return false; // always allow entering data in retrospective mode
    if (section === 'pregnancy') return !!ongoingPregnancy;
    else if (section === 'contraception') return !!ongoingContraception;
    else return false;
  };

  return (
    <React.Fragment>
      <PlatformConditional platform={platforms.filter(p => p !== 'mgravis')}>
        <HistorySection
          headerText={
            <FormattedMessage
              id={platform && platform === 'ninmt' ? 'comorbidity.diagnoses' : 'comorbidity.comorbidities'}
            />
          }
          hasHistoryRows={true}
          newButton={
            <DocumentCreationButton
              name="comorbidity"
              text={platform && platform === 'ninmt' ? 'comorbidity.newDiagnosis' : 'comorbidity.newComorbidity'}
              onClick={startEdit({}, 'comorbidity')}
              width={15}
              height={3}
              fontSize={14}
              alternate={true}
            />
          }
        >
          {platform && platform === 'ninmt' ? (
            <React.Fragment>
              {['ninmtSymptom', 'otherDisease'].map(header => {
                const comorbiditiesFiltered = comorbidities.filter(c =>
                  header === 'ninmtSymptom' ? c.ninmtSymptom : !c.ninmtSymptom,
                );
                return (
                  comorbiditiesFiltered.length > 0 && (
                    <HistoryRow key={header} headerText={<FormattedMessage id={`comorbidity.${header}`} />}>
                      <HistoryRowListing
                        documents={comorbiditiesFiltered}
                        headers={
                          <React.Fragment>
                            <Item xs={3}>
                              <FormattedMessage id="comorbidity.diagnosisDate" />
                            </Item>
                            <Item xs={9}>
                              <FormattedMessage id="comorbidity.ICD-10" />
                            </Item>
                          </React.Fragment>
                        }
                        contentFormat={(d: (typeof comorbiditiesFiltered)[0]): React.JSX.Element => (
                          <React.Fragment>
                            <HistoryRowDataComorbidity d={d} diagnosis={diagnosis} startEdit={startEdit} />
                          </React.Fragment>
                        )}
                      />
                    </HistoryRow>
                  )
                );
              })}
            </React.Fragment>
          ) : (
            comorbidities.map(c => (
              <HistoryRow
                headerText={
                  c.date
                    ? formatPartialDate(c.date) + `${c.endDate ? ' - ' + formatPartialDate(c.endDate) : ''}`
                    : undefined
                }
                key={c._id}
                active={!isPartialDate(c.endDate) || !isEnded(c.endDate)}
                rowButton={<HistoryRowControls document={c} startEdit={startEdit} />}
                controlsMargin={false}
              >
                <HistoryRowDataComorbidity d={c} diagnosis={diagnosis} startEdit={startEdit} />
              </HistoryRow>
            ))
          )}
        </HistorySection>
      </PlatformConditional>
      <PlatformConditional platform={['sma', 'dmd', 'mgravis']}>
        <HistorySection
          headerText={<FormattedMessage id="comorbidity.hospitalizations" />}
          hasHistoryRows={true}
          newButton={
            <DocumentCreationButton
              name="hospitalization"
              text={'comorbidity.newHospitalization'}
              onClick={startEdit({}, 'hospitalization')}
              width={15}
              height={3}
              fontSize={14}
              alternate={true}
            />
          }
        >
          {platform === 'mgravis' ? (
            <MgravisHospitalizationHistory documents={mgravisHospitalizations} startEdit={startEdit} />
          ) : (
            <>
              {hospitalizations.map(h => (
                <HistoryRow
                  headerText={
                    h.date
                      ? formatPartialDate(h.date) + `${h.endDate ? ' - ' + formatPartialDate(h.endDate) : ''}`
                      : undefined
                  }
                  key={h._id}
                  active={!h.endDate || !h.endDate[0]}
                  rowButton={<HistoryRowControls document={h} startEdit={startEdit} />}
                  controlsMargin={false}
                >
                  <HistoryRowDataHospitalization d={h} />
                </HistoryRow>
              ))}
            </>
          )}
        </HistorySection>
      </PlatformConditional>
      <PlatformConditional platform="epilepsy">
        <React.Fragment>
          {[...extraEpSections].map(section => (
            <HistorySection
              key={section.headerText}
              headerText={<FormattedMessage id={`comorbidity.${section.headerText}`} />}
              hasHistoryRows={true}
              newButton={
                <DocumentCreationButton
                  name={section.type}
                  text={`comorbidity.${section.buttonText}`}
                  onClick={startEdit({}, section.type)}
                  width={15}
                  height={3}
                  fontSize={14}
                  alternate={true}
                  disabled={disableAddNewOf(section.type)}
                  disabledTooltip={<FormattedMessage id="comorbidity.endOngoingToAddNew" />}
                />
              }
            >
              {[...section.documents].map(d => (
                <HistoryRow
                  headerText={
                    d.date
                      ? formatPartialDate(d.date) + `${d.endDate ? ' - ' + formatPartialDate(d.endDate) : ''}`
                      : undefined
                  }
                  key={d._id}
                  active={!d.endDate || !d.endDate[0]}
                  rowButton={<HistoryRowControls document={d} startEdit={startEdit} />}
                  controlsMargin={false}
                >
                  {section.type === 'pregnancy' ? (
                    <HistoryRowDataPregnancy />
                  ) : section.type === 'contraception' ? (
                    <HistoryRowDataContraception d={d as IDocumentWithTypeV2<'contraception'>} />
                  ) : (
                    <></>
                  )}
                </HistoryRow>
              ))}
            </HistorySection>
          ))}
        </React.Fragment>
      </PlatformConditional>
      <PlatformConditional platform="sleepApnea">
        <HistorySection
          headerText={<FormattedMessage id="comorbidity.sickLeaves" />}
          hasHistoryRows={true}
          newButton={
            <DocumentCreationButton
              name="sickLeave"
              text={'comorbidity.newSickLeave'}
              onClick={startEdit({}, 'sickLeave')}
              width={15}
              height={3}
              fontSize={14}
              alternate={true}
            />
          }
        >
          {sickLeaves.map(s => (
            <HistoryRow
              headerText={
                s.date
                  ? formatPartialDate(s.date) + `${s.endDate ? ' - ' + formatPartialDate(s.endDate) : ''}`
                  : undefined
              }
              key={s._id}
              active={!s.endDate || !s.endDate[0]}
              rowButton={<HistoryRowControls document={s} startEdit={startEdit} />}
              controlsMargin={false}
            >
              <HistoryRowDataSickLeave d={s} />
            </HistoryRow>
          ))}
        </HistorySection>
      </PlatformConditional>
    </React.Fragment>
  );
};
const History = connect(stateToHistoryProps)(_History);

const Comorbidity = ({ documents }: IOwnProps): React.JSX.Element => {
  const platform = useSelector((s: { session: ISessionStore }) => s.session.platforms?.selected);
  const headerId = getSectionTitle('comorbidity', platform);

  const documentsFiltered = filterDocumentsByType(documents, [
    'comorbidity',
    'hospitalization',
    'pregnancy',
    'contraception',
    'sickLeave',
  ]);

  return (
    <FormEditingHandler name="comorbidity" documents={documents}>
      {(editing, startEdit, formData): React.JSX.Element => {
        const type = documentsFiltered.find(d => d._id === editing)?._type;
        return (
          <DocumentWrapper>
            <DocumentHeader
              name="comorbidity"
              headerId={
                {
                  comorbidity: platform === 'ninmt' ? 'comorbidity.diagnoses' : 'comorbidity.comorbidity',
                  hospitalization: 'comorbidity.hospitalization',
                  pregnancy: 'comorbidity.pregnancy',
                  contraception: 'comorbidity.useOfContraceptive',
                  sickLeave: 'comorbidity.sickLeave',
                  '': `comorbidity.${headerId}`,
                }[type ?? ''] || `comorbidity.${headerId}`
              }
              editing={editing}
            />
            {editing ? (
              <Form
                documents={documentsFiltered}
                document={documentsFiltered.find(d => d._id === editing)}
                formData={formData}
                editing={!!editing}
                medicationNames={getMedicationNames(documents)}
                diagnosis={getDiagnosisCodes(documents)}
                platform={platform}
              />
            ) : (
              <History
                documents={documentsFiltered.sort((n1, n2) => sortPartialDate(n2.date, n1.date))}
                startEdit={startEdit}
                diagnosis={getDiagnosisCodes(documents)}
              />
            )}
          </DocumentWrapper>
        );
      }}
    </FormEditingHandler>
  );
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<TRelevantDocumentType>[]; // Medication documents are only used to get medication names for adverse effect medication selection and Diagnosis used at Comorbidity form if patient has J96.1 or J96.9
}

export default Comorbidity;
